(() => {
  const SECTION_ID = 'cart-drawer';
  const SECTION_WRAPPER_ID = `shopify-section-${SECTION_ID}`;

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.backdrop = this.querySelector('[data-cart-backdrop]');
      this.panel = this.querySelector('.siliane-cart-drawer__panel');
      this.bindGlobal();
      this.bindInner();
    }

    open() {
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      // Focus close button after transition
      setTimeout(() => {
        this.querySelector('[data-cart-close]')?.focus();
      }, 250);
    }

    close() {
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    bindGlobal() {
      // Open on any element with [data-cart-open]
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-cart-open]');
        if (opener) {
          e.preventDefault();
          this.open();
        }
      });

      // Backdrop click closes
      this.backdrop?.addEventListener('click', () => this.close());

      // ESC key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
          this.close();
        }
      });

      // Intercept ALL add-to-cart forms across the page
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[action$="/cart/add"], form[action*="/cart/add"]');
        if (!form) return;
        e.preventDefault();
        this.addToCart(form);
      });
    }

    bindInner() {
      // Events on buttons inside the drawer (event delegation on the drawer itself)
      this.addEventListener('click', (e) => {
        const close = e.target.closest('[data-cart-close]');
        if (close) { this.close(); return; }

        const remove = e.target.closest('[data-cart-remove]');
        if (remove) { this.changeLine(remove.dataset.lineKey, 0); return; }

        const inc = e.target.closest('[data-cart-increment]');
        if (inc) {
          const item = this.querySelector(`[data-line-key="${inc.dataset.lineKey}"]`);
          const qtyEl = item?.querySelector('[data-cart-qty] span');
          const current = parseInt(qtyEl?.textContent || '0', 10);
          this.changeLine(inc.dataset.lineKey, current + 1);
          return;
        }

        const dec = e.target.closest('[data-cart-decrement]');
        if (dec) {
          const item = this.querySelector(`[data-line-key="${dec.dataset.lineKey}"]`);
          const qtyEl = item?.querySelector('[data-cart-qty] span');
          const current = parseInt(qtyEl?.textContent || '0', 10);
          this.changeLine(dec.dataset.lineKey, Math.max(0, current - 1));
          return;
        }
      });
    }

    async addToCart(form) {
      const submitBtn = form.querySelector('[data-product-submit]');
      const label = submitBtn?.querySelector('[data-submit-label]');
      const originalLabel = label?.textContent;
      if (label) label.textContent = 'Ajout…';
      if (submitBtn) submitBtn.disabled = true;

      try {
        const formData = new FormData(form);
        formData.append('sections', SECTION_ID);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'application/javascript' }
        });
        if (!res.ok) throw new Error('Add to cart failed');
        const data = await res.json();
        if (data.sections && data.sections[SECTION_ID]) {
          this.renderSection(data.sections[SECTION_ID]);
        } else {
          await this.refresh();
        }
        this.updateHeaderCount();
        this.open();
      } catch (err) {
        console.error(err);
        alert('Impossible d\'ajouter au panier. Réessayez.');
      } finally {
        if (label && originalLabel) label.textContent = originalLabel;
        if (submitBtn) submitBtn.disabled = false;
      }
    }

    async changeLine(key, quantity) {
      try {
        const body = JSON.stringify({
          id: key,
          quantity: quantity,
          sections: SECTION_ID
        });
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body
        });
        if (!res.ok) throw new Error('Cart update failed');
        const data = await res.json();
        if (data.sections && data.sections[SECTION_ID]) {
          this.renderSection(data.sections[SECTION_ID]);
        } else {
          await this.refresh();
        }
        this.updateHeaderCount();
      } catch (err) {
        console.error(err);
      }
    }

    async refresh() {
      try {
        const res = await fetch(`/?sections=${SECTION_ID}`);
        if (!res.ok) throw new Error('Section refresh failed');
        const data = await res.json();
        if (data[SECTION_ID]) this.renderSection(data[SECTION_ID]);
      } catch (err) {
        console.error(err);
      }
    }

    renderSection(html) {
      // The API returns the full section wrapper. Extract the inner content and swap.
      const tmp = document.createElement('div');
      tmp.innerHTML = html.trim();
      const fresh = tmp.querySelector('[data-cart-drawer-inner]');
      const current = this.querySelector('[data-cart-drawer-inner]');
      if (fresh && current) {
        current.replaceWith(fresh);
      }
    }

    async updateHeaderCount() {
      try {
        const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        if (!res.ok) return;
        const cart = await res.json();
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = el.closest('.siliane-cart-drawer__count') ? `(${cart.item_count})` : cart.item_count;
          if (cart.item_count === 0 && el.classList.contains('siliane-header__cart-count')) {
            el.remove();
          }
        });
        // Also update header cart badge (create if missing when cart goes from 0 → n)
        const headerCart = document.querySelector('.siliane-header__cart');
        if (headerCart && cart.item_count > 0 && !headerCart.querySelector('[data-cart-count]')) {
          const badge = document.createElement('span');
          badge.className = 'siliane-header__cart-count';
          badge.setAttribute('data-cart-count', '');
          badge.textContent = cart.item_count;
          headerCart.appendChild(badge);
        }
      } catch (err) {
        console.error(err);
      }
    }
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', CartDrawer);
  }
})();
